import { Inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { MerchantService } from '@galaxy/billing';
import { FREE_SUBSCRIPTION_ID } from '@galaxy/partner';
import { PartnerService } from '../core/partner.service';

@Injectable()
export class VendastaPaymentsGuard {
  constructor(
    private router: Router,
    @Inject('PARTNER_ID') readonly partnerId$: Observable<string>,
    private readonly partnerService: PartnerService,
    private merchantService: MerchantService,
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    const hasVendastaPayments$: Observable<boolean> = this.partnerId$.pipe(
      switchMap((partnerId) => this.merchantService.get(partnerId)),
      map((merchant) => !!merchant.stripeConnectId),
      catchError(() => of(false)),
    );
    const isFreeTier$: Observable<boolean> = this.partnerService.subscriptionTier$.pipe(
      map((currentTier) => currentTier.id === FREE_SUBSCRIPTION_ID),
    );

    return combineLatest([isFreeTier$, hasVendastaPayments$]).pipe(
      map(([isFreeTier, hasVendastaPayments]) => hasVendastaPaymentsAccess(isFreeTier, hasVendastaPayments)),
      map(
        (hasAccess) =>
          hasAccess ||
          this.router.createUrlTree(['/restricted/', 'vendasta-payments'], { queryParams: { page: state.url } }),
      ),
    );
  }
}

// Returns true if the partner has access to Vendasta Payments
//
// Partners on Free, or on a 14-day trial do not have access to Vendasta Payments unless they've already setup their
// Stripe account. This means that Partners who have been downgraded to free will still have access to the
// Vendasta Payments if they've been using it.
//
// isFreeTier is a boolean that indicates the Partner is on a free tier subscription.
// onTrial is a boolean indicating that the Partner is currently on their 14-day free trial of a subscription tier.
// hasVendastaPayments indicates that the Partner has already setup Vendasta Payments.
export function hasVendastaPaymentsAccess(isFreeTier: boolean, hasVendastaPayments: boolean): boolean {
  return hasVendastaPayments || !isFreeTier;
}
